import React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { reduceLayerTwoData, reduceCreativesData, reduceNameBasedData, shouldShowTotalActivities } from 'helpers/data';
import { getActivitiesSettings } from 'helpers/objectiveSettings';
import { toolTipPointFormatterGenerator } from 'helpers/charts';
import { generateCalloutComponents } from 'helpers/componentGenerators';

import { fetchActivities } from 'services/activities/duck';
import { fetchChannel, fetchChannelCallouts, fetchChannelCreatives, fetchChannelDynamicCreatives, fetchChannelTargetingTactics } from 'services/channel/duck';
import { fetchLayerTwoChannelSummary } from 'services/layerTwo/duck';
import { fetchMeta as fetchEntityMeta } from 'services/entities/duck';
import { fetchTacticPlacements } from 'services/tactics/duck';
import { setObjectiveShadow } from 'services/config/duck';

import { LayerTwoDashboardComponent } from 'containers/dashboards/DashboardBaseComponent';
import ObjectiveSwitcher from 'components/molecules/ObjectiveSwitcher';
import { Grid, Row } from 'react-bootstrap';
import ChannelTitle from 'components/atoms/layout/ChannelTitle';
import { NoChannelDataTile } from 'components/molecules/ErrorTile';
import RenderingTile from 'components/molecules/RenderingTile';
import ExportPDF from 'containers/buttons/ExportPDF';
import CreateNewNote from 'containers/buttons/CreateNewNote';
import ViewEntity from 'containers/buttons/ViewEntity';
import Note from 'containers/annotations/NoteContainer';

// Charts
import ActivitiesStackedAreaChart from 'components/molecules/charts/ActivitiesStackedAreaChart';
import GroupedBarChart from 'components/molecules/charts/GroupedBarChart';
import TwoAxisBarWithLineChart from 'components/molecules/charts/TwoAxisBarWithLineChart';

// Tables
import ActivitiesTable from 'components/molecules/tables/ActivitiesTable';
import PlacementsTable from 'components/molecules/tables/PlacementsTable';
import ProtectedCreativesReports from 'components/organisms/ProtectedCreativesReports';
import ProtectedDynamicCreativesReports from 'components/organisms/ProtectedDynamicCreativesReports';
import ProtectedTargetingTacticsTable from 'components/organisms/ProtectedTargetingTacticsTable';

// LayerTwo Charts & Tables
import LayerTwoSummaryTable from 'components/molecules/tables/LayerTwoSummaryTable';

class LayerThreeNativeContainer extends LayerTwoDashboardComponent {
    constructor(props) {
        super(props, 'native');
    }

    makeRequestsForData() {
        const entityId = this.getCurrentEntityId();
        const channel = this.getCurrentChannel();
        const payload = this.createPayload(entityId, channel);

        // HACK: Only trigger an API call if the data has not been previously pulled
        if (this.props.entityMeta.id !== entityId) {
            this.props.fetchEntityMeta(payload);
        }

        this.props.fetchLayerTwoChannelSummary(payload);
        this.props.fetchChannel(payload);
        this.props.fetchChannelCallouts(payload);
        this.props.fetchChannelCreatives(payload);
        this.props.fetchChannelDynamicCreatives(payload);
        this.props.fetchTacticPlacements(payload);
        this.props.fetchActivities(payload);
        this.props.fetchChannelTargetingTactics(payload);
    }

    render() {
        const channel = this.getCurrentChannel();
        const stub = `l2-${channel}`;
        const {
            backfillBuckets,
            xAxisLabel,
            entityObjective,
            showTotalActivities,
            showTargetingTactics,
            showConversionRevenue,
            selectedTimeRange,
            entityObjectiveLabelCode,
        } = this.props;
        const {
            isLabelCoded,
            eCPAActivitiesOrClicks,
            eCPAActivitiesOrClicksTerm,
            showActivitiesData,
            calloutTitle,
            primaryActivityField,
            primaryActivityFieldTerm,
            ecpaTerm,
            helpKeySuffix,
            singularTerm,
        } = getActivitiesSettings(entityObjective, entityObjectiveLabelCode);

        const storeData = this.props.layerTwo[channel];
        const hasLayerTwoSummaryData = get(storeData, 'summary.hasData', false);

        let childSummaryTableData = [];
        if (hasLayerTwoSummaryData) {
            const reducedData = reduceLayerTwoData(storeData.summary);
            childSummaryTableData = reducedData.childSummaryTableData;
        }

        const hasCalloutData = get(this.props, 'layerThreeChannel.callouts.params.hasData', false);
        const hasLayerThreePlacementsData = get(this.props, 'layerThreePlacements.params.hasData', null);
        const hasLayerThreeTargetingTacticsData = get(this.props, 'layerThreeChannel.targetingTactics.params.hasData', null);
        const hasLayerThreeChannelData = get(this.props, 'layerThreeChannel.hasData', false);
        const hasCreativesData = get(this.props, 'layerThreeChannel.creatives.params.hasData', null);
        const hasDynamicCreativesData = get(this.props, 'layerThreeChannel.dynamicCreatives.params.hasData', null);

        const callouts = get(this.props, 'layerThreeChannel.callouts.data', {});
        const calloutWindow = get(this.props, 'layerThreeChannel.callouts.params.timeRanges.previous.windowSize', null);
        const layerThreeChannelData = get(this.props, 'layerThreeChannel.data', {});
        const placements = get(this.props, 'layerThreePlacements', {});
        const targetingTactics = get(this.props, 'layerThreeChannel.targetingTactics', {});
        const creatives = get(this.props, 'layerThreeChannel.creatives', {});

        const activities = get(this.props, 'layerThreeActivities', {});
        const hasActivities = get(this.props, 'layerThreeActivities.hasData', null);
        const activitiesRenderFlag = hasActivities && (showActivitiesData || showTotalActivities);
        const userId = get(this.props, 'currentUser.id', null);

        let placementsData = null;
        const placementsParams = get(this.props, 'layerThreePlacements.params', {});
        if (hasLayerThreePlacementsData) {
            placementsData = reduceNameBasedData({
                data: placements,
                namesArrayKey: 'placements',
                injectedKey: 'placementName'
            });
        }

        let targetingTacticsData = {};
        const targetingTacticsParams = get(this.props, 'layerThreeChannelData.targetingTactics.params', {});
        if (hasLayerThreeTargetingTacticsData) {
            targetingTacticsData = reduceNameBasedData({
                data: targetingTactics,
                namesArrayKey: 'targetingTactics',
                injectedKey: 'targetingTacticName'
            });
        }

        let creativesData = null;
        const creativesParams = get(this.props, 'layerThreeChannel.creatives.params', {});
        if (hasCreativesData) {
            creativesData = reduceCreativesData(creatives);
        }

        const isChannelAvailable = get(this.props, 'entityMeta.channelAvailability.native', false);
        const aliasDescriptor = get(this.props, 'entityMeta.childAliasNames.pick', 'LOCATION');

        const calloutSettings = [
            {
                key: 'impressions',
                title: 'Impressions',
                format: '0,0'
            },
            {
                key: eCPAActivitiesOrClicks,
                helpKey: `${eCPAActivitiesOrClicks}${helpKeySuffix}`,
                title: calloutTitle,
                format: '0,0'
            },
        ];

        if (showActivitiesData) {
            calloutSettings.push({
                key: 'ecpa',
                helpKey: `ecpa${helpKeySuffix}`,
                title: entityObjectiveLabelCode ? `Average ${ecpaTerm}` : 'eCPA',
                format: '$0,0.00',
                invert: true
            });
        } else {
            calloutSettings.push({
                key: 'ctr',
                title: 'CTR',
                format: '0.00%'
            });
        }

        const calloutComponents = generateCalloutComponents({
            stub, calloutSettings, hasCalloutData, callouts, calloutWindow, isLabelCoded
        });

        const ChannelActions = <React.Fragment>
            <CreateNewNote />
            <ViewEntity />
            {isChannelAvailable && <ExportPDF template={stub} />}
        </React.Fragment>;

        return isChannelAvailable ? (
            <Grid fluid>
                <ChannelTitle print={ChannelActions}>
                    Native
                </ChannelTitle>
                {this.props.enableObjectiveSwitching && <ObjectiveSwitcher
                    setObjectiveShadow={this.props.setObjectiveShadow}
                    fetchEntityMeta={this.props.fetchEntityMeta}
                    entityId={this.getCurrentEntityId()}
                    objective={this.props.entityObjective}
                    objectiveLabelCode={this.props.entityObjectiveLabelCode}
                />}
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                {/* Call Outs */}
                <Row>
                    {calloutComponents}
                </Row>
                {/* Native Performance */}
                {hasLayerThreeChannelData ? (
                    <TwoAxisBarWithLineChart
                        title="NATIVE PERFORMANCE"
                        exportName={`${stub}-native-performance`}
                        data={layerThreeChannelData}
                        yBar="impressions"
                        yBarOptions={{ formatByType: 'number' }}
                        yLine={eCPAActivitiesOrClicks}
                        yLineTitle={eCPAActivitiesOrClicksTerm}
                        yLineOptions={{ formatByType: 'number' }}
                        toolTipOptions={{
                            pointFormatter: toolTipPointFormatterGenerator
                        }}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        animateDuration={2000}
                        tactic={this.state.channel}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreeChannelData} />
                )}

                {/* Child Tables */}
                {hasLayerTwoSummaryData ? (
                    <LayerTwoSummaryTable
                        title={`${aliasDescriptor} OVERVIEW`}
                        channel={this.getCurrentChannel()}
                        aliasDescriptor={aliasDescriptor}
                        data={childSummaryTableData}
                        exportName={`${stub}-table-performance`}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['impressions', 'clicks', 'ctr', 'spend'] :
                                undefined
                        }
                        cacheKey={`${stub}-table-performance-${userId}-${this.getCurrentEntityId()}`}
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerTwoSummaryData} />
                )}

                {/* Activities */}
                {activitiesRenderFlag ? (
                    <ActivitiesStackedAreaChart
                        title={`${singularTerm.toUpperCase()} TRENDS`}
                        exportName={`${stub}-activity-trends`}
                        exportLimit={10}
                        data={activities.data}
                        keys={activities.rankings[primaryActivityField].map(d => d.name).reverse()}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        yAxisLabel={primaryActivityFieldTerm}
                        y={primaryActivityField}
                        colors={this.props.theme.orderedColors}
                        animateDuration={2000} />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {activitiesRenderFlag ? (
                    <ActivitiesTable
                        title={`${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-website-activity-breakdown`}
                        data={activities}
                        showTotalActivities={showTotalActivities}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        objectives={[entityObjective]}
                        filterField={showTotalActivities ? 'total_activities' : primaryActivityField}
                    />
                ) : (
                    <RenderingTile height='400px' show={activitiesRenderFlag} />
                )}

                {/* Placements */}
                {hasLayerThreePlacementsData ? (
                    <GroupedBarChart
                        title="PLACEMENT PERFORMANCE"
                        exportName={`${stub}-placement-performance`}
                        data={placementsData.graph}
                        keys={placementsData.sortedKeys}
                        xAxisLabel={xAxisLabel}
                        backfillBuckets={backfillBuckets}
                        y={eCPAActivitiesOrClicks}
                        yAxisLabel={eCPAActivitiesOrClicksTerm}
                        animateDuration={2000}
                        xAxisType="datetime" />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreePlacementsData} />
                )}

                {hasLayerThreePlacementsData ? (
                    <PlacementsTable
                        title={`PLACEMENT ${singularTerm.toUpperCase()} BREAKDOWN`}
                        exportName={`${stub}-placement-activity-breakdown`}
                        data={placementsData.table}
                        params={placementsParams}
                        hasData={hasLayerThreePlacementsData}
                        showTotalActivities={showTotalActivities}
                        showConversionRevenue={showConversionRevenue}
                        entityObjectiveLabelCode={entityObjectiveLabelCode}
                        defaultSorted={entityObjective === 'ctr' ? [{
                            id: 'clicks',
                            desc: true
                        }] : undefined}
                        accessors={
                            entityObjective === 'ctr' ?
                                ['placement', 'impressions', 'clicks', 'ctr', 'spend'] :
                                undefined
                        }
                    />
                ) : (
                    <RenderingTile height='400px' show={hasLayerThreePlacementsData} />
                )}

                <ProtectedTargetingTacticsTable
                    title="TARGETING TACTICS BREAKDOWN"
                    exportName={`${stub}-targeting-tactics-breakdown`}
                    tableType="targetingTactics"
                    data={targetingTacticsData.table}
                    params={targetingTacticsParams}
                    hasData={hasLayerThreeTargetingTacticsData}
                    showTotalActivities={showTotalActivities}
                    showTargetingTactics={showTargetingTactics}
                    showConversionRevenue={showConversionRevenue}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['targetingTactic', 'impressions', 'clicks', 'ctr'] :
                            undefined
                    }
                />

                <ProtectedCreativesReports
                    exportName={`${stub}-creatives`}
                    hasCreativesData={hasCreativesData}
                    creativesData={creativesData}
                    creativesParams={creativesParams}
                    eCPAActivitiesOrClicks={eCPAActivitiesOrClicks}
                    showTotalActivities={showTotalActivities}
                    showConversionRevenue={showConversionRevenue}
                    eCPAActivitiesOrClicksTerm={eCPAActivitiesOrClicksTerm}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                    accessors={
                        entityObjective === 'ctr' ?
                            ['label', 'size', 'impressions', 'clicks', 'ctr'] :
                            undefined
                    }
                />

                <ProtectedDynamicCreativesReports
                    exportName={`${stub}-dynamic-creatives`}
                    hasDynamicCreativesData={hasDynamicCreativesData}
                    dynamicCreatives={get(this.props, 'layerThreeChannel.dynamicCreatives', {})}
                    dynamicProfiles={get(this.props, 'entityMeta.dynamicProfiles', {})}
                    entityObjective={entityObjective}
                    entityObjectiveLabelCode={entityObjectiveLabelCode}
                />
            </Grid>
        ) : (
            <Grid fluid={true}>
                <ChannelTitle print={ChannelActions}>
                    Native
                </ChannelTitle>
                <Note
                    dashboard={this.getCurrentChannel()}
                    entityId={this.getCurrentEntityId()}
                    start={selectedTimeRange.apiConfig.start().format('YYYY-MM-DD')}
                    end={selectedTimeRange.apiConfig.end().format('YYYY-MM-DD')}
                />
                <NoChannelDataTile channel='Native' />
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    theme: state.theme,
    entityMeta: state.entities.meta,
    enableObjectiveSwitching: get(state, 'entities.meta.meta.enableObjectiveSwitching', false),
    entityObjective: get(state, 'entities.meta.meta.programmaticObjective.global', 'ecpa'),
    entityObjectiveLabelCode: get(state, 'entities.meta.meta.programmaticObjective.labelCode', null),
    showTotalActivities: shouldShowTotalActivities(state),
    showTargetingTactics: get(state, 'entities.meta.meta.showTargetingTactics', false),
    showConversionRevenue: get(state, 'entities.meta.meta.showConversionRevenue', false),
    layerTwo: state.layerTwo,
    selectedTimeRange: state.config.selectedTimeRange,
    xAxisLabel: get(state, 'config.timeBuckets.config.xAxisLabel', 'Date'),
    backfillBuckets: get(state, 'config.timeBuckets.config.buckets', []),

    // LayerThree Services
    layerThreeChannel: state.channel.native,
    layerThreePlacements: state.tactics.placements,
    layerThreeActivities: state.activities.native
});

export default connect(mapStateToProps, {
    fetchActivities,
    fetchChannel,
    fetchChannelCallouts,
    fetchChannelCreatives,
    fetchChannelDynamicCreatives,
    fetchChannelTargetingTactics,
    fetchEntityMeta,
    fetchLayerTwoChannelSummary,
    fetchTacticPlacements,
    setObjectiveShadow,
})(LayerThreeNativeContainer);



// WEBPACK FOOTER //
// ./src/containers/dashboards/LayerTwoNativeContainer.js